import { Box, List, Section, Text } from '@factorialco/deprecated-design-system'

import UserAvatar from 'modules/core/components/UserAvatar'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'

import type { EditorInfo } from 'modules/surveys/pages/Show/hooks'
import { useSurveyShowContext } from 'modules/surveys/pages/Show/hooks'
import type Survey from 'modules/surveys/store/models/v2/Survey'

import EditorRightActions from './EditorRightActions'

interface ItemProps {
  supervisorId: string
  survey: Survey
  authorId: string
  editors: EditorInfo[]
}

function Item({ supervisorId, survey, authorId, editors }: ItemProps) {
  const {
    access: { id: currentAccessId },
  } = useCurrentSession()
  const editor = editors.find((e) => e.accessId === supervisorId)
  const userIsAuthor = supervisorId === authorId
  const currentUserIsAuthor = userIsAuthor && currentAccessId === authorId

  if (!editor) return null

  const fullName = getFullDisplayName({ firstName: editor.firstName, lastName: editor.lastName })

  return (
    <List.Item>
      <Box key={editor.accessId} paddingY="s16">
        <Box flexDirection="row" gap="s16" alignItems="center">
          <UserAvatar size="s40" user={{ avatar: editor.avatar, fullName }} />
          <Box overflowX="hidden">
            <Text.Normal weight="medium" ellipsis>
              {fullName}
            </Text.Normal>
          </Box>
        </Box>
      </Box>
      {!currentUserIsAuthor && (
        <EditorRightActions
          userIsAuthor={userIsAuthor}
          supervisorId={supervisorId}
          survey={survey}
        />
      )}
    </List.Item>
  )
}

interface ItemsProps {
  supervisorsIds: string[]
  survey: Survey
  authorId: string
  editors: EditorInfo[]
}

function Items({ supervisorsIds, survey, authorId, editors }: ItemsProps) {
  if (!supervisorsIds.length) {
    return (
      <Box aria-label="List of editors">
        <Text.Normal>{i18n.t('surveys.form.managers.blank_slate')}</Text.Normal>
      </Box>
    )
  }

  return (
    <Box aria-label="List of editors">
      <List>
        {supervisorsIds.map((supervisorId) => (
          <Item
            key={supervisorId}
            supervisorId={supervisorId}
            survey={survey}
            authorId={authorId}
            editors={editors}
          />
        ))}
      </List>
    </Box>
  )
}

interface Props {
  survey: Survey
}

export default function EditorsSection({ survey }: Props) {
  const { navigation } = useNavigation()
  const { editors } = useSurveyShowContext()
  const authorId = survey.attributes.author_id
  const route = navigation.processes.surveys.id.configuration.addManager.fullPath({
    id: survey.attributes.id,
  })
  const addEditor = {
    to: route,
    label: i18n.t('surveys.form.managers.add_manager'),
  }

  return (
    <Section
      illustration="people-add"
      title={i18n.t('surveys.form.managers.title')}
      description={i18n.t('surveys.form.managers.description')}
      action={addEditor}
    >
      <Items
        supervisorsIds={survey.attributes.supervisor_ids}
        survey={survey}
        authorId={authorId}
        editors={editors}
      />
    </Section>
  )
}
